import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Pencil, Trash2, X, Save, Building2, Search, ChevronRight, ChevronLeft,
  Phone, Mail, Users, CreditCard, ShoppingCart, FileText, Receipt, Tag,
  Download, Upload, ArrowLeft, Clock, Eye, ExternalLink, Loader2
} from 'lucide-react';
import CsvImportModal from '../components/CsvImportModal';
import CreateButton from '../components/CreateButton';

const ITEMS_PER_PAGE = 50;
const PRICES_PER_PAGE = 50;
const IVA_MAP = { 1: 4, 2: 10, 3: 21 };

const PROVINCIAS = [
  'Álava','Albacete','Alicante','Almería','Asturias','Ávila','Badajoz','Barcelona',
  'Burgos','Cáceres','Cádiz','Cantabria','Castellón','Ciudad Real','Córdoba','La Coruña',
  'Cuenca','Gerona','Granada','Guadalajara','Guipúzcoa','Huelva','Huesca','Islas Baleares',
  'Jaén','León','Lérida','Lugo','Madrid','Málaga','Murcia','Navarra','Orense','Palencia',
  'Las Palmas','Pontevedra','La Rioja','Salamanca','Segovia','Sevilla','Soria','Tarragona',
  'Santa Cruz de Tenerife','Teruel','Toledo','Valencia','Valladolid','Vizcaya','Zamora',
  'Zaragoza','Ceuta','Melilla'
];

const EMPTY_FORM = {
  codigo: '', razon_social: '', alias: '', cif: '', calle: '', localidad: '',
  provincia: '', c_postal: '', telefono1: '', telefono2: '',
  mail: '', mail_admin: '', mail_admin2: '', mail_reclamacion: '',
  mail_pedidos: '', mail_precios: '',
  fax: '', contacto: '', auxiliar: '', clinica_matriz: 0, id_comercial: 0,
  estado: 1, iban: '', cuenta_contable: '', forma_pago: '',
  dia_pago1: '', dia_pago2: '', dia_pago3: '', dia_pago4: '',
};

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';

// ── Section Header ──
const SectionHeader = ({ title }) => (
  <div className="mb-4 mt-6 first:mt-0">
    <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">{title}</h4>
    <div className="border-b border-gray-200 mt-1" />
  </div>
);

// ── Form Field ──
const FormField = ({ label, required, children, className = '' }) => (
  <div className={className}>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}{required && ' *'}
    </label>
    {children}
  </div>
);

// ── Clinica Matriz Search Input ──
const ClinicaMatrizInput = ({ value, displayName, onChange, onClear, token, API_URL }) => {
  const [query, setQuery] = useState(displayName || '');
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const timeoutRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setQuery(displayName || '');
  }, [displayName]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const doSearch = useCallback(async (term) => {
    if (!term || term.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await fetch(`${API_URL}/clients/search-matriz?q=${encodeURIComponent(term)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setResults(data.clients || data || []);
      }
    } catch { /* ignore */ }
    setSearching(false);
  }, [API_URL, token]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setShowDropdown(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => doSearch(val), 300);
  };

  const handleSelect = (client) => {
    onChange(client.id, client.razon_social);
    setQuery(client.razon_social);
    setShowDropdown(false);
    setResults([]);
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    onClear();
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="flex">
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => { if (results.length) setShowDropdown(true); }}
          placeholder="Buscar cliente matriz..."
          className={inputClass + ' rounded-r-none'}
        />
        {value ? (
          <button
            type="button"
            onClick={handleClear}
            className="px-3 border border-l-0 border-gray-300 rounded-r-md text-gray-400 hover:text-red-500 bg-white"
          >
            <X size={14} />
          </button>
        ) : null}
      </div>
      {showDropdown && (results.length > 0 || searching) && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
          {searching && (
            <div className="px-3 py-2 text-sm text-gray-400">Buscando...</div>
          )}
          {results.map(c => (
            <button
              key={c.id}
              type="button"
              onClick={() => handleSelect(c)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors"
            >
              <span className="font-medium text-gray-800">{c.razon_social}</span>
              <span className="text-gray-400 ml-2">#{c.id}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════
// ── MAIN COMPONENT ──
// ══════════════════════════════════════════════════════
const ClientManagement = () => {
  const navigate = useNavigate();
  const { token, API_URL, user } = useAuth();

  // Sidebar state
  const [clients, setClients] = useState([]);
  const [totalClients, setTotalClients] = useState(0);
  const [loading, setLoading] = useState(true);
  const [formaPagoOptions, setFormaPagoOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [page, setPage] = useState(1);

  // Main panel state
  const [mode, setMode] = useState('detail'); // 'detail' | 'form' | 'precios' | 'pedidos'
  // #12: asignar precio a una referencia NUEVA (sin precio aun para el cliente)
  const [showAssignPrice, setShowAssignPrice] = useState(false);
  const [assignSearch, setAssignSearch] = useState('');
  const [assignResults, setAssignResults] = useState([]);
  const [assignSelected, setAssignSelected] = useState(null);
  const [assignPrecio, setAssignPrecio] = useState('');
  const [assignSaving, setAssignSaving] = useState(false);
  const [activeClient, setActiveClient] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Linked clients
  const [linkedClients, setLinkedClients] = useState([]);
  const [loadingLinked, setLoadingLinked] = useState(false);

  // Client orders
  const [clientOrders, setClientOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Clinica Matriz display name
  const [matrizDisplayName, setMatrizDisplayName] = useState('');

  // Prices state
  const [prices, setPrices] = useState([]);
  const [pricesTotalPages, setPricesTotalPages] = useState(1);
  const [pricesPage, setPricesPage] = useState(1);
  const [pricesTotal, setPricesTotal] = useState(0);
  const [pricesSearch, setPricesSearch] = useState('');
  const [pricesSearchTimeout, setPricesSearchTimeout] = useState(null);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [editingPriceValue, setEditingPriceValue] = useState('');

  // Facturacion state
  const [clientFacturas, setClientFacturas] = useState([]);
  const [facturasTotal, setFacturasTotal] = useState(0);
  const [facturasTotalPages, setFacturasTotalPages] = useState(1);
  const [facturasPage, setFacturasPage] = useState(1);
  const [facturasSearch, setFacturasSearch] = useState('');
  const [facturasSearchTimeout, setFacturasSearchTimeout] = useState(null);
  const [loadingFacturas, setLoadingFacturas] = useState(false);
  const [previewFactura, setPreviewFactura] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);

  const isAdmin = user && (user.permisos === 'admin' || user.permisos === 'superadmin');
  const [showImportModal, setShowImportModal] = useState(false);

  const handleImportClients = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_URL}/clients/import`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Error al importar');
    fetchClients(page, searchTerm);
    return data;
  };

  // ── Fetch clients (sidebar) ──
  const fetchClients = useCallback(async (currentPage = 1, search = '') => {
    setLoading(true);
    try {
      let url;
      if (search.trim()) {
        url = `${API_URL}/clients/search?q=${encodeURIComponent(search)}&page=${currentPage}&limit=${ITEMS_PER_PAGE}`;
      } else {
        url = `${API_URL}/clients?page=${currentPage}&limit=${ITEMS_PER_PAGE}`;
      }
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al cargar clientes');
      const data = await res.json();
      setClients(data.clients || []);
      setTotalClients(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => { fetchClients(page, searchTerm); }, [page]);

  // Load forma_pago options once
  useEffect(() => {
    fetch(`${API_URL}/clients/forma-pago`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setFormaPagoOptions(data.data || []); })
      .catch(() => {});
  }, [API_URL, token]);

  useEffect(() => {
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      setPage(1);
      fetchClients(1, searchTerm);
    }, 400);
    setSearchTimeout(timeout);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  // ── Fetch linked clients ──
  useEffect(() => {
    if (!activeClient) { setLinkedClients([]); return; }
    const fetchLinked = async () => {
      setLoadingLinked(true);
      try {
        const res = await fetch(`${API_URL}/clients/${activeClient.id}/linked`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Error al cargar clientes vinculados');
        const data = await res.json();
        setLinkedClients(data.linkedClients || data.clients || []);
      } catch (err) {
        setError(err.message);
        setLinkedClients([]);
      } finally {
        setLoadingLinked(false);
      }
    };
    fetchLinked();
  }, [activeClient, API_URL, token]);

  // ── Fetch client orders ──
  const fetchClientOrders = useCallback(async () => {
    if (!activeClient) { setClientOrders([]); return; }
    setLoadingOrders(true);
    try {
      const res = await fetch(`${API_URL}/clients/${activeClient.id}/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClientOrders(data.orders || []);
      }
    } catch (err) { console.error(err); }
    finally { setLoadingOrders(false); }
  }, [activeClient, API_URL, token]);

  useEffect(() => { fetchClientOrders(); }, [fetchClientOrders]);

  // ── Acciones de pedido (mismas que Gestion de Pedidos) ──
  const [orderActionBusy, setOrderActionBusy] = useState(false);

  const downloadBlob = async (url, fallbackName) => {
    const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
    if (!res.ok) throw new Error('No se pudo descargar el archivo');
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const m = disposition.match(/filename="?([^"]+)"?/);
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = m ? m[1] : fallbackName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const handleOrderPdf = async (orderId) => {
    setOrderActionBusy(true);
    try { await downloadBlob(`${API_URL}/orders/${orderId}/pdf`, `pedido_${orderId}.pdf`); }
    catch (err) { alert(err.message); }
    finally { setOrderActionBusy(false); }
  };

  const handleOrderCsv = async (orderId, embalaje) => {
    setOrderActionBusy(true);
    try {
      const url = embalaje ? `${API_URL}/orders/${orderId}/csv?embalaje=1` : `${API_URL}/orders/${orderId}/csv`;
      await downloadBlob(url, `pedido_${orderId}.csv`);
      fetchClientOrders(); // el CSV marca el pedido como Enviado
    } catch (err) { alert(err.message); }
    finally { setOrderActionBusy(false); }
  };

  const handleOrderDelete = async (orderId) => {
    if (!window.confirm(`¿Seguro que desea eliminar el pedido #${orderId}? Esta acción no se puede deshacer.`)) return;
    setOrderActionBusy(true);
    try {
      const res = await fetch(`${API_URL}/orders/${orderId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'No se pudo eliminar el pedido');
      }
      fetchClientOrders();
    } catch (err) { alert(err.message); }
    finally { setOrderActionBusy(false); }
  };

  // ── Fetch clinica_matriz display name for detail view ──
  const [detailMatrizName, setDetailMatrizName] = useState('');
  useEffect(() => {
    if (!activeClient || !activeClient.clinica_matriz) {
      setDetailMatrizName('');
      return;
    }
    const fetchMatrizName = async () => {
      try {
        const res = await fetch(`${API_URL}/clients/${activeClient.clinica_matriz}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const client = data.client || data;
          setDetailMatrizName(client.razon_social || `#${activeClient.clinica_matriz}`);
        }
      } catch {
        setDetailMatrizName(`#${activeClient.clinica_matriz}`);
      }
    };
    fetchMatrizName();
  }, [activeClient?.id, activeClient?.clinica_matriz, API_URL, token]);

  // ── Reset mode when switching clients ──
  useEffect(() => {
    if (activeClient) {
      setMode('pedidos');
      setPricesSearch('');
      setPricesPage(1);
      setFacturasSearch('');
      setFacturasPage(1);
    }
  }, [activeClient?.id]);

  // ── Prices fetching ──
  const fetchPrices = useCallback(async (clientId, currentPage = 1, search = '') => {
    setLoadingPrices(true);
    try {
      const params = new URLSearchParams({ page: currentPage, limit: PRICES_PER_PAGE });
      if (search.trim()) params.set('search', search);
      const res = await fetch(`${API_URL}/prices/client/${clientId}?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al cargar precios');
      const data = await res.json();
      setPrices(data.prices || []);
      setPricesTotal(data.total || 0);
      setPricesTotalPages(data.totalPages || 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingPrices(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    if (mode === 'precios' && activeClient) {
      fetchPrices(activeClient.id, pricesPage, pricesSearch);
    }
  }, [mode, activeClient, pricesPage]);

  useEffect(() => {
    if (mode !== 'precios' || !activeClient) return;
    if (pricesSearchTimeout) clearTimeout(pricesSearchTimeout);
    const timeout = setTimeout(() => {
      setPricesPage(1);
      fetchPrices(activeClient.id, 1, pricesSearch);
    }, 400);
    setPricesSearchTimeout(timeout);
    return () => clearTimeout(timeout);
  }, [pricesSearch]);

  // ── Facturacion fetching ──
  // Baja el PDF de la factura del backend (con auth) como blob -> el PDF real, no
  // la landing de la SPA (antes se apuntaba a comprassanitarias.com/facturas/*,
  // que la SPA no sirve). El backend permite al admin ver cualquier factura.
  const fetchFacturaBlobUrl = async (factura) => {
    const res = await fetch(`${API_URL}/commercial/facturas/${encodeURIComponent(factura.nombre_archivo)}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('No se pudo abrir la factura');
    return URL.createObjectURL(await res.blob());
  };

  const openFacturaPdf = async (factura) => {
    try { window.open(await fetchFacturaBlobUrl(factura), '_blank', 'noopener'); }
    catch (e) { setError(e.message); }
  };

  useEffect(() => {
    if (!previewFactura) return undefined;
    let objUrl = null;
    let cancelled = false;
    setPreviewPdfUrl(null);
    fetchFacturaBlobUrl(previewFactura)
      .then((url) => { if (cancelled) URL.revokeObjectURL(url); else { objUrl = url; setPreviewPdfUrl(url); } })
      .catch(() => { if (!cancelled) setError('No se pudo cargar la factura'); });
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); setPreviewPdfUrl(null); };
  }, [previewFactura]); // eslint-disable-line react-hooks/exhaustive-deps
  const FACTURAS_PER_PAGE = 25;

  const fetchClientFacturas = useCallback(async (clientId, currentPage = 1, search = '') => {
    setLoadingFacturas(true);
    try {
      const params = new URLSearchParams({ page: currentPage, limit: FACTURAS_PER_PAGE, cliente: clientId });
      if (search.trim()) params.set('search', search);
      const res = await fetch(`${API_URL}/commercial/facturas?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al cargar facturas');
      const data = await res.json();
      setClientFacturas(data.facturas || []);
      setFacturasTotal(data.pagination?.total || data.total || 0);
      setFacturasTotalPages(data.pagination?.totalPages || 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingFacturas(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    if (mode === 'facturacion' && activeClient) {
      fetchClientFacturas(activeClient.id, facturasPage, facturasSearch);
    }
  }, [mode, activeClient, facturasPage]);

  useEffect(() => {
    if (mode !== 'facturacion' || !activeClient) return;
    if (facturasSearchTimeout) clearTimeout(facturasSearchTimeout);
    const timeout = setTimeout(() => {
      setFacturasPage(1);
      fetchClientFacturas(activeClient.id, 1, facturasSearch);
    }, 400);
    setFacturasSearchTimeout(timeout);
    return () => clearTimeout(timeout);
  }, [facturasSearch]);

  // ── Price inline edit ──
  const handlePriceSave = async (priceId) => {
    const numValue = parseFloat(editingPriceValue);
    if (isNaN(numValue) || numValue < 0) { setError('Precio no válido'); return; }
    try {
      const res = await fetch(`${API_URL}/prices/${priceId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ precio: numValue })
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const reason = errData.message || errData.error || (res.status === 404 ? 'El precio no fue encontrado' : 'Error desconocido del servidor');
        throw new Error(`Error al actualizar precio: ${reason}`);
      }
      setPrices(prev => prev.map(p => p.id === priceId ? { ...p, precio: numValue } : p));
      setEditingPriceId(null);
      setEditingPriceValue('');
    } catch (err) {
      setError(err.message);
    }
  };

  // ── Export / Import ──
  const handleExportExcel = async () => {
    if (!activeClient) return;
    try {
      const res = await fetch(`${API_URL}/prices/client/${activeClient.id}/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al exportar precios');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `precios_${activeClient.razon_social.replace(/\s+/g, '_')}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      setError(err.message);
    }
  };

  // Descarga PDF de precios por cliente (replica el generate_pdf.php del PHP legacy)
  const handleExportPdf = async () => {
    if (!activeClient) return;
    try {
      const res = await fetch(`${API_URL}/prices/client/${activeClient.id}/export-pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al exportar PDF de precios');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `precios_${activeClient.razon_social.replace(/\s+/g, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file || !activeClient) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_URL}/prices/client/${activeClient.id}/import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) throw new Error('Error al importar precios');
      const data = await res.json();
      alert(data.message + (data.errors ? `\n\nErrores:\n${data.errors.join('\n')}` : ''));
      fetchPrices(activeClient.id, pricesPage, pricesSearch);
    } catch (err) {
      setError(err.message);
    }
    e.target.value = '';
  };

  // ── Form open helpers ──
  const openCreate = () => {
    setEditingClient(null);
    setForm({ ...EMPTY_FORM });
    setMatrizDisplayName('');
    setMode('form');
  };

  const openEdit = async (client) => {
    setEditingClient(client);
    setForm({
      razon_social: client.razon_social || '',
      alias: client.alias || '',
      cif: client.cif || '',
      calle: client.calle || '',
      localidad: client.localidad || '',
      provincia: client.provincia || '',
      c_postal: client.c_postal || '',
      telefono1: client.telefono1 || '',
      telefono2: client.telefono2 || '',
      mail: client.mail || '',
      mail_admin: client.mail_admin || '',
      mail_admin2: client.mail_admin2 || '',
      mail_reclamacion: client.mail_reclamacion || '',
      mail_pedidos: client.mail_pedidos || '',
      mail_precios: client.mail_precios || '',
      fax: client.fax || '',
      contacto: client.contacto || '',
      auxiliar: client.auxiliar || '',
      clinica_matriz: client.clinica_matriz || 0,
      id_comercial: client.id_comercial || 0,
      estado: client.estado != null ? client.estado : 1,
      iban: client.iban || '',
      cuenta_contable: client.cuenta_contable || '',
      forma_pago: client.forma_pago || '',
      dia_pago1: client.dia_pago != null && String(client.dia_pago) !== '0' && String(client.dia_pago) !== '' ? String(client.dia_pago) : '',
      dia_pago2: client.dia_pago_2 != null && String(client.dia_pago_2) !== '0' && String(client.dia_pago_2) !== '' ? String(client.dia_pago_2) : '',
      dia_pago3: client.dia_pago_3 != null && String(client.dia_pago_3) !== '0' && String(client.dia_pago_3) !== '' ? String(client.dia_pago_3) : '',
      dia_pago4: client.dia_pago_4 != null && String(client.dia_pago_4) !== '0' && String(client.dia_pago_4) !== '' ? String(client.dia_pago_4) : '',
    });
    // Resolve clinica_matriz display name
    if (client.clinica_matriz) {
      try {
        const res = await fetch(`${API_URL}/clients/${client.clinica_matriz}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const c = data.client || data;
          setMatrizDisplayName(c.razon_social || '');
        }
      } catch {
        setMatrizDisplayName('');
      }
    } else {
      setMatrizDisplayName('');
    }
    setMode('form');
  };

  // ── Save client ──
  const handleSave = async () => {
    // ── Validaciones de campos ──
    const errors = [];
    if (!form.razon_social.trim()) errors.push('La Razón Social es obligatoria');
    if (!form.cif.trim()) errors.push('El CIF es obligatorio');

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emailFields = [
      { key: 'mail', label: 'Email Principal' },
      { key: 'mail_admin', label: 'Email Admin' },
      { key: 'mail_admin2', label: 'Email Admin 2' },
      { key: 'mail_reclamacion', label: 'Email Reclamación' },
      { key: 'mail_pedidos', label: 'Email Pedidos' },
      { key: 'mail_precios', label: 'Email Precios' },
    ];
    emailFields.forEach(({ key, label }) => {
      if (form[key] && form[key].trim() && !emailRegex.test(form[key].trim())) {
        errors.push(`${label} no tiene un formato válido`);
      }
    });

    if (form.iban && form.iban.trim()) {
      const iban = form.iban.replace(/\s/g, '');
      if (iban.length < 15 || iban.length > 34) errors.push('El IBAN no tiene una longitud válida');
    }

    const diaPagoFields = [
      { key: 'dia_pago1', label: 'Día Pago 1' },
      { key: 'dia_pago2', label: 'Día Pago 2' },
      { key: 'dia_pago3', label: 'Día Pago 3' },
      { key: 'dia_pago4', label: 'Día Pago 4' },
    ];
    diaPagoFields.forEach(({ key, label }) => {
      if (form[key] !== '' && form[key] != null && form[key] !== '0') {
        const day = parseInt(form[key]);
        if (isNaN(day) || day < 1 || day > 31) errors.push(`${label} debe ser un número entre 1 y 31`);
      }
    });

    if (errors.length > 0) { setError(errors.join('. ')); return; }

    setSaving(true);
    setError('');
    try {
      const url = editingClient ? `${API_URL}/clients/${editingClient.id}` : `${API_URL}/clients`;
      const method = editingClient ? 'PUT' : 'POST';
      // Map frontend field names to backend (dia_pagoN → dia_pago / dia_pago_N)
      const { dia_pago1, dia_pago2, dia_pago3, dia_pago4, codigo, ...rest } = form;
      const payload = {
        ...rest,
        dia_pago: dia_pago1 || null,
        dia_pago_2: dia_pago2 || null,
        dia_pago_3: dia_pago3 || null,
        dia_pago_4: dia_pago4 || null,
      };
      // El codigo de cliente ES el id: solo se manda al CREAR (en edicion no cambia)
      if (!editingClient && codigo !== '' && codigo != null) payload.id = codigo;
      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        let reason = data.message || data.error;
        if (!reason) {
          switch (res.status) {
            case 409: reason = 'Ya existe un cliente con ese CIF'; break;
            case 400: reason = data.errors ? data.errors.join('. ') : 'Revise los datos del formulario'; break;
            case 403: reason = 'No tiene permisos para realizar esta acción'; break;
            case 404: reason = 'El cliente no fue encontrado, puede que haya sido eliminado'; break;
            case 500: reason = 'Error interno del servidor, contacte con el administrador'; break;
            default: reason = `Error del servidor (código ${res.status})`;
          }
        }
        throw new Error(`Error al ${editingClient ? 'actualizar' : 'crear'} cliente: ${reason}`);
      }
      fetchClients(page, searchTerm);
      if (editingClient && activeClient && editingClient.id === activeClient.id) {
        setActiveClient({ ...activeClient, ...form });
      }
      if (!editingClient && data.client) {
        setActiveClient(data.client);
      }
      setMode('detail');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Delete client ──
  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este cliente?')) return;
    try {
      const res = await fetch(`${API_URL}/clients/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const errData = !res.ok ? await res.json().catch(() => ({})) : {};
      if (!res.ok) {
        const reason = errData.message || errData.error || (res.status === 409 ? 'El cliente tiene pedidos o datos asociados' : 'Error desconocido del servidor');
        throw new Error(`Error al eliminar cliente: ${reason}`);
      }
      if (activeClient && activeClient.id === id) setActiveClient(null);
      fetchClients(page, searchTerm);
    } catch (err) {
      setError(err.message);
    }
  };

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const getPriceColorClass = (precio, precioCoste) => {
    if (!precioCoste || precioCoste <= 0) return 'bg-green-50 text-green-700';
    if (precio < precioCoste) return 'bg-red-50 text-red-700';
    const markup = ((precio - precioCoste) / precioCoste) * 100;
    if (markup < 30) return 'bg-amber-50 text-amber-700';
    return 'bg-green-50 text-green-700';
  };

  const totalPages = Math.ceil(totalClients / ITEMS_PER_PAGE);

  const actionButtons = [
    { key: 'pedidos', label: 'Ver Pedidos', icon: FileText, onClick: () => setMode('pedidos') },
    { key: 'datos', label: 'Datos Cliente', icon: Building2, onClick: () => setMode('detail') },
    { key: 'precios', label: 'Precios', icon: Tag, onClick: () => setMode('precios') },
    { key: 'pedido', label: 'Nuevo Pedido', icon: ShoppingCart, onClick: () => navigate(`/pedidos/nuevo?cliente=${activeClient.id}`) },
    { key: 'facturacion', label: 'Facturación', icon: Receipt, onClick: () => setMode('facturacion') },
  ];

  // ── Handle sidebar client click ──
  const handleClientClick = (client) => {
    setActiveClient(client);
    setMode('pedidos');
  };

  // ══════════════════════════════════════════════════
  // ── RENDER: Detail View ──
  // ══════════════════════════════════════════════════
  const renderDetailView = () => {
    if (!activeClient) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="p-6 rounded-full w-24 h-24 mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,153,255,0.08)' }}>
              <Building2 className="w-12 h-12" style={{ color: '#0099FF' }} />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Gestión de Clientes</h2>
            <p className="text-gray-500">Selecciona un cliente de la lista para ver sus datos</p>
          </div>
        </div>
      );
    }

    return (
      <>
        {/* Client header bar */}
        <div className="bg-white shadow-sm border-b border-gray-200 p-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 rounded-full" style={{ backgroundColor: 'rgba(0,153,255,0.08)' }}>
                <Building2 className="w-8 h-8" style={{ color: '#0099FF' }} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{activeClient.razon_social}</h1>
                {activeClient.alias && <p className="text-gray-500">{activeClient.alias}</p>}
                <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                  {activeClient.telefono1 && (
                    <div className="flex items-center"><Phone size={14} className="mr-1" />{activeClient.telefono1}</div>
                  )}
                  {activeClient.mail && (
                    <div className="flex items-center"><Mail size={14} className="mr-1" />{activeClient.mail}</div>
                  )}
                  {activeClient.cif && (
                    <div className="flex items-center"><CreditCard size={14} className="mr-1" />{activeClient.cif}</div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => openEdit(activeClient)}
                className="px-4 py-2 text-white rounded-lg transition-colors duration-200 text-sm flex items-center hover:opacity-90"
                style={{ backgroundColor: '#0099FF' }}
              >
                <Pencil size={14} className="mr-2" /> Editar
              </button>
              <button
                onClick={() => handleDelete(activeClient.id)}
                className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors duration-200 text-sm flex items-center"
              >
                <Trash2 size={14} className="mr-2" /> Eliminar
              </button>
            </div>
          </div>

          {/* Action buttons row */}
          <div className="flex items-center gap-3 mt-4">
            {actionButtons.map(btn => {
              const Icon = btn.icon;
              const isActive = (btn.key === 'precios' && mode === 'precios') || (btn.key === 'pedidos' && mode === 'pedidos') || (btn.key === 'datos' && mode === 'detail') || (btn.key === 'facturacion' && mode === 'facturacion');
              return (
                <button
                  key={btn.key}
                  onClick={btn.onClick}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-200"
                  style={btn.key === 'pedido'
                    ? { borderColor: '#16a34a', backgroundColor: '#16a34a', color: '#ffffff' }
                    : isActive
                    ? { borderColor: '#0099FF', backgroundColor: 'rgba(0,153,255,0.08)', color: '#0077CC' }
                    : { borderColor: '#e5e7eb', backgroundColor: '#ffffff', color: '#374151' }
                  }
                  onMouseEnter={(e) => {
                    if (btn.key === 'pedido') { e.currentTarget.style.backgroundColor = '#15803d'; e.currentTarget.style.borderColor = '#15803d'; }
                    else if (!isActive) { e.currentTarget.style.borderColor = '#0099FF'; e.currentTarget.style.color = '#0077CC'; }
                  }}
                  onMouseLeave={(e) => {
                    if (btn.key === 'pedido') { e.currentTarget.style.backgroundColor = '#16a34a'; e.currentTarget.style.borderColor = '#16a34a'; }
                    else if (!isActive) { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#374151'; }
                  }}
                >
                  <Icon size={16} />
                  {btn.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Scrollable detail content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Datos Generales */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
              <Building2 size={18} className="mr-2" style={{ color: '#0099FF' }} />
              Datos del Cliente
            </h3>

            <SectionHeader title="Datos Generales" />
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              {[
                ['Razón Social', activeClient.razon_social],
                ['Alias', activeClient.alias],
                ['CIF', activeClient.cif],
                ['Estado', activeClient.estado === 0 ? 'Inactivo' : 'Activo'],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="text-xs text-gray-500 mb-1">{label}</p>
                  <p className="font-medium text-gray-900">{value || '\u2014'}</p>
                </div>
              ))}
            </div>

            <SectionHeader title="Dirección" />
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              {[
                ['Calle', activeClient.calle],
                ['Localidad', activeClient.localidad],
                ['Provincia', activeClient.provincia],
                ['Código Postal', activeClient.c_postal],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="text-xs text-gray-500 mb-1">{label}</p>
                  <p className="font-medium text-gray-900">{value || '\u2014'}</p>
                </div>
              ))}
            </div>

            <SectionHeader title="Contacto" />
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              {[
                ['Teléfono 1', activeClient.telefono1],
                ['Teléfono 2', activeClient.telefono2],
                ['Fax', activeClient.fax],
                ['Contacto', activeClient.contacto],
                ['Auxiliar', activeClient.auxiliar],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="text-xs text-gray-500 mb-1">{label}</p>
                  <p className="font-medium text-gray-900">{value || '\u2014'}</p>
                </div>
              ))}
            </div>

            <SectionHeader title="Emails" />
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              {[
                ['Email Principal', activeClient.mail],
                ['Email Admin', activeClient.mail_admin],
                ['Email Admin 2', activeClient.mail_admin2],
                ['Email Reclamación', activeClient.mail_reclamacion],
                ['Email Pedidos', activeClient.mail_pedidos],
                ['Email Precios', activeClient.mail_precios],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="text-xs text-gray-500 mb-1">{label}</p>
                  <p className="font-medium text-gray-900">{value || '\u2014'}</p>
                </div>
              ))}
            </div>

            <SectionHeader title="Datos Comerciales" />
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              {[
                ['Responsable Comercial', activeClient.id_comercial || '\u2014'],
                ['Clínica Matriz', activeClient.clinica_matriz ? (detailMatrizName || `#${activeClient.clinica_matriz}`) : '\u2014'],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="text-xs text-gray-500 mb-1">{label}</p>
                  <p className="font-medium text-gray-900">{value || '\u2014'}</p>
                </div>
              ))}
            </div>

            <SectionHeader title="Datos de Pago" />
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              {[
                ['IBAN', activeClient.iban],
                ['Cuenta Contable', activeClient.cuenta_contable],
                ['Forma de Pago', activeClient.forma_pago],
                ['Día de Pago 1', activeClient.dia_pago],
                ['Día de Pago 2', activeClient.dia_pago_2],
                ['Día de Pago 3', activeClient.dia_pago_3],
                ['Día de Pago 4', activeClient.dia_pago_4],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="text-xs text-gray-500 mb-1">{label}</p>
                  <p className="font-medium text-gray-900">{value || '\u2014'}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </>
    );
  };

  // ══════════════════════════════════════════════════
  // ── RENDER: Pedidos View ──
  // ══════════════════════════════════════════════════
  const renderPedidosView = () => {
    if (!activeClient) return null;

    const statusLabel = (e) => { switch(e) { case 1: return 'Abierto'; case 2: return 'Confirmado'; case 3: return 'Enviado'; case 4: return 'Envío parcial'; default: return 'Desconocido'; } };
    const statusColor = (e) => { switch(e) { case 1: return 'bg-blue-100 text-blue-800'; case 2: return 'bg-green-100 text-green-800'; case 3: return 'bg-purple-100 text-purple-800'; case 4: return 'bg-yellow-100 text-yellow-800'; default: return 'bg-gray-100 text-gray-600'; } };
    const statusIcon = (e) => { switch(e) { case 1: return <Clock size={14} />; case 2: return <ShoppingCart size={14} />; case 3: return <FileText size={14} />; case 4: return <FileText size={14} />; default: return null; } };

    return (
      <>
        {/* Client header with actions */}
        <div className="bg-white shadow-sm border-b border-gray-200 p-6 flex-shrink-0">
          {/* same header content as detail view - client info + action buttons */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 rounded-full" style={{ backgroundColor: 'rgba(0,153,255,0.08)' }}>
                <Building2 className="w-8 h-8" style={{ color: '#0099FF' }} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{activeClient.razon_social}</h1>
                {activeClient.alias && <p className="text-gray-500">{activeClient.alias}</p>}
                <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                  {activeClient.telefono1 && (<div className="flex items-center"><Phone size={14} className="mr-1" />{activeClient.telefono1}</div>)}
                  {activeClient.mail && (<div className="flex items-center"><Mail size={14} className="mr-1" />{activeClient.mail}</div>)}
                  {activeClient.cif && (<div className="flex items-center"><CreditCard size={14} className="mr-1" />{activeClient.cif}</div>)}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button onClick={() => openEdit(activeClient)} className="px-4 py-2 text-white rounded-lg transition-colors duration-200 text-sm flex items-center hover:opacity-90" style={{ backgroundColor: '#0099FF' }}>
                <Pencil size={14} className="mr-2" /> Editar
              </button>
              <button onClick={() => handleDelete(activeClient.id)} className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors duration-200 text-sm flex items-center">
                <Trash2 size={14} className="mr-2" /> Eliminar
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-4">
            {actionButtons.map(btn => {
              const Icon = btn.icon;
              const isActive = (btn.key === 'precios' && mode === 'precios') || (btn.key === 'pedidos' && mode === 'pedidos') || (btn.key === 'datos' && mode === 'detail') || (btn.key === 'facturacion' && mode === 'facturacion');
              return (
                <button key={btn.key} onClick={btn.onClick}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-200"
                  style={btn.key === 'pedido' ? { borderColor: '#16a34a', backgroundColor: '#16a34a', color: '#ffffff' } : isActive ? { borderColor: '#0099FF', backgroundColor: 'rgba(0,153,255,0.08)', color: '#0077CC' } : { borderColor: '#e5e7eb', backgroundColor: '#ffffff', color: '#374151' }}
                  onMouseEnter={(e) => { if (btn.key === 'pedido') { e.currentTarget.style.backgroundColor = '#15803d'; e.currentTarget.style.borderColor = '#15803d'; } else if (!isActive) { e.currentTarget.style.borderColor = '#0099FF'; e.currentTarget.style.color = '#0077CC'; }}}
                  onMouseLeave={(e) => { if (btn.key === 'pedido') { e.currentTarget.style.backgroundColor = '#16a34a'; e.currentTarget.style.borderColor = '#16a34a'; } else if (!isActive) { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#374151'; }}}
                >
                  <Icon size={16} /> {btn.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Orders table */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {loadingOrders ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: '#0099FF' }}></div>
              </div>
            ) : clientOrders.length === 0 ? (
              <div className="text-center py-12">
                <FileText size={28} className="mx-auto mb-2 text-gray-300" />
                <p className="text-sm text-gray-500">No hay pedidos para este cliente</p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pedido</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Creado por</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Lineas</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Importe</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {clientOrders.map(order => (
                    <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">#{order.id}</span>
                        {order.npedido && order.npedido !== 'S/N' && <span className="text-xs text-gray-400 ml-2">({order.npedido})</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(order.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{order.usuario_nombre || '\u2014'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{order.total_lineas}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">{Number(order.total_importe).toFixed(2)} €</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor(order.estado)}`}>
                          {statusIcon(order.estado)}
                          {statusLabel(order.estado)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => navigate(`/pedidos/nuevo?order=${order.id}${order.estado !== 1 ? '&readonly=1' : ''}`)}
                            title={order.estado === 1 ? 'Editar pedido' : 'Ver pedido'}
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          >
                            {order.estado === 1 ? <Pencil size={16} /> : <Eye size={16} />}
                          </button>
                          <button
                            onClick={() => handleOrderPdf(order.id)}
                            disabled={orderActionBusy}
                            title="Descargar PDF"
                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                          >
                            <FileText size={16} />
                          </button>
                          <button
                            onClick={() => handleOrderCsv(order.id, false)}
                            disabled={orderActionBusy}
                            title="Descargar CSV"
                            className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                          >
                            <Download size={16} />
                          </button>
                          <button
                            onClick={() => handleOrderDelete(order.id)}
                            disabled={orderActionBusy}
                            title="Eliminar pedido"
                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </>
    );
  };

  // ══════════════════════════════════════════════════
  // ── RENDER: Form View ──
  // ══════════════════════════════════════════════════
  const renderFormView = () => (
    <>
      {/* Form header */}
      <div className="bg-white shadow-sm border-b border-gray-200 p-6 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 rounded-full" style={{ backgroundColor: 'rgba(0,153,255,0.08)' }}>
              <Building2 className="w-8 h-8" style={{ color: '#0099FF' }} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {editingClient ? `Editar Cliente: ${editingClient.razon_social}` : 'Nuevo Cliente'}
              </h1>
              <p className="text-sm text-gray-500">
                {editingClient ? 'Modifica los datos del cliente' : 'Completa los datos para crear un nuevo cliente'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form body (scrollable) */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">

          {/* Datos Generales */}
          <SectionHeader title="Datos Generales" />
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-3">
            {!editingClient && (
              <FormField label="Código de cliente" className="col-span-2 lg:col-span-1">
                <input type="number" value={form.codigo} onChange={(e) => updateField('codigo', e.target.value)} className={inputClass} placeholder="Auto si se deja vacío" />
              </FormField>
            )}
            <FormField label="Razón Social" required className="col-span-2 lg:col-span-3">
              <input type="text" value={form.razon_social} onChange={(e) => updateField('razon_social', e.target.value)} className={inputClass} />
            </FormField>
            <FormField label="Alias" className="col-span-2">
              <input type="text" value={form.alias} onChange={(e) => updateField('alias', e.target.value)} className={inputClass} />
            </FormField>
            <FormField label="CIF" required className="col-span-1">
              <input type="text" value={form.cif} onChange={(e) => updateField('cif', e.target.value)} className={inputClass} />
            </FormField>
            <FormField label="Calle" className="col-span-2 lg:col-span-3">
              <input type="text" value={form.calle} onChange={(e) => updateField('calle', e.target.value)} className={inputClass} />
            </FormField>
            <FormField label="Localidad" className="col-span-1">
              <input type="text" value={form.localidad} onChange={(e) => updateField('localidad', e.target.value)} className={inputClass} />
            </FormField>
            <FormField label="Provincia" className="col-span-1">
              <select value={form.provincia} onChange={(e) => updateField('provincia', e.target.value)} className={inputClass}>
                <option value="">Seleccionar...</option>
                {PROVINCIAS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </FormField>
            <FormField label="C.P." className="col-span-1">
              <input type="text" value={form.c_postal} onChange={(e) => updateField('c_postal', e.target.value)} className={inputClass} />
            </FormField>
          </div>

          {/* Contacto y Emails */}
          <SectionHeader title="Contacto y Emails" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-3">
            <FormField label="Teléfono 1">
              <input type="text" value={form.telefono1} onChange={(e) => updateField('telefono1', e.target.value)} className={inputClass} />
            </FormField>
            <FormField label="Teléfono 2">
              <input type="text" value={form.telefono2} onChange={(e) => updateField('telefono2', e.target.value)} className={inputClass} />
            </FormField>
            <FormField label="Fax">
              <input type="text" value={form.fax} onChange={(e) => updateField('fax', e.target.value)} className={inputClass} />
            </FormField>
            <FormField label="Contacto">
              <input type="text" value={form.contacto} onChange={(e) => updateField('contacto', e.target.value)} className={inputClass} />
            </FormField>
            <FormField label="Auxiliar">
              <input type="text" value={form.auxiliar} onChange={(e) => updateField('auxiliar', e.target.value)} className={inputClass} />
            </FormField>
            <FormField label="Email Principal">
              <input type="email" value={form.mail} onChange={(e) => updateField('mail', e.target.value)} className={inputClass} />
            </FormField>
            <FormField label="Email Admin">
              <input type="email" value={form.mail_admin} onChange={(e) => updateField('mail_admin', e.target.value)} className={inputClass} />
            </FormField>
            <FormField label="Email Admin 2">
              <input type="email" value={form.mail_admin2} onChange={(e) => updateField('mail_admin2', e.target.value)} className={inputClass} />
            </FormField>
            <FormField label="Email Reclamación">
              <input type="email" value={form.mail_reclamacion} onChange={(e) => updateField('mail_reclamacion', e.target.value)} className={inputClass} />
            </FormField>
            <FormField label="Email Pedidos">
              <input type="email" value={form.mail_pedidos} onChange={(e) => updateField('mail_pedidos', e.target.value)} className={inputClass} />
            </FormField>
            <FormField label="Email Precios">
              <input type="email" value={form.mail_precios} onChange={(e) => updateField('mail_precios', e.target.value)} className={inputClass} />
            </FormField>
          </div>

          {/* Datos Comerciales y Pago */}
          <SectionHeader title="Datos Comerciales y Pago" />
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-3">
            <FormField label="R. Comercial">
              <input
                type="number"
                value={form.id_comercial}
                onChange={(e) => updateField('id_comercial', parseInt(e.target.value) || 0)}
                className={inputClass}
              />
            </FormField>
            <FormField label="Clínica Matriz" className="col-span-2">
              <ClinicaMatrizInput
                value={form.clinica_matriz}
                displayName={matrizDisplayName}
                onChange={(id, name) => {
                  updateField('clinica_matriz', id);
                  setMatrizDisplayName(name);
                }}
                onClear={() => {
                  updateField('clinica_matriz', 0);
                  setMatrizDisplayName('');
                }}
                token={token}
                API_URL={API_URL}
              />
            </FormField>
            <FormField label="Estado">
              <select value={form.estado} onChange={(e) => updateField('estado', parseInt(e.target.value))} className={inputClass}>
                <option value={1}>Activo</option>
                <option value={0}>Inactivo</option>
              </select>
            </FormField>
            <FormField label="IBAN" className="col-span-2">
              <input type="text" value={form.iban} onChange={(e) => updateField('iban', e.target.value)} className={inputClass} />
            </FormField>
            <FormField label="Cuenta Contable">
              <input type="text" value={form.cuenta_contable} onChange={(e) => updateField('cuenta_contable', e.target.value)} className={inputClass} />
            </FormField>
            <FormField label="Forma de Pago" className="col-span-2">
              <select value={form.forma_pago || ''} onChange={(e) => updateField('forma_pago', e.target.value)} className={inputClass}>
                <option value="">— Seleccionar —</option>
                {formaPagoOptions.map(fp => (
                  <option key={fp.id} value={fp.id}>{fp.descripcion}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Día Pago 1">
              <input type="number" value={form.dia_pago1} onChange={(e) => updateField('dia_pago1', e.target.value)} className={inputClass} />
            </FormField>
            <FormField label="Día Pago 2">
              <input type="number" value={form.dia_pago2} onChange={(e) => updateField('dia_pago2', e.target.value)} className={inputClass} />
            </FormField>
            <FormField label="Día Pago 3">
              <input type="number" value={form.dia_pago3} onChange={(e) => updateField('dia_pago3', e.target.value)} className={inputClass} />
            </FormField>
            <FormField label="Día Pago 4">
              <input type="number" value={form.dia_pago4} onChange={(e) => updateField('dia_pago4', e.target.value)} className={inputClass} />
            </FormField>
          </div>
        </div>
      </div>

      {/* Form footer */}
      <div className="bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3 flex-shrink-0">
        <button
          onClick={() => {
            if (activeClient) { setMode('pedidos'); } else { setMode('pedidos'); }
          }}
          className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
        >
          Cancelar
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 rounded-md text-sm font-medium text-white transition-colors flex items-center gap-2 disabled:opacity-50"
          style={{ backgroundColor: '#0099FF' }}
        >
          {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save size={14} />}
          {editingClient ? 'Actualizar' : 'Crear'}
        </button>
      </div>
    </>
  );

  // ══════════════════════════════════════════════════
  // ── RENDER: Precios View ──
  // ══════════════════════════════════════════════════
  // #12: buscar producto para asignar precio a una referencia nueva
  const searchAssignProduct = async (term) => {
    setAssignSearch(term);
    setAssignSelected(null);
    if (!term || term.trim().length < 2) { setAssignResults([]); return; }
    try {
      const res = await fetch(`${API_URL}/prices/product-search?search=${encodeURIComponent(term.trim())}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { const data = await res.json(); setAssignResults(data.products || []); }
    } catch { /* ignore */ }
  };

  const saveAssignPrice = async () => {
    const precioNum = parseFloat(assignPrecio);
    if (!assignSelected || assignPrecio === '' || isNaN(precioNum) || precioNum < 0) return;
    setAssignSaving(true);
    try {
      // cmatriz igual que el backend: clinica_matriz o el id del cliente si es 0
      const cmatriz = activeClient.clinica_matriz || activeClient.id;
      const res = await fetch(`${API_URL}/prices`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ idarticulo: assignSelected.id, cmatriz, precio: precioNum })
      });
      if (!res.ok) throw new Error('Error al asignar el precio');
      setShowAssignPrice(false);
      setAssignSearch(''); setAssignResults([]); setAssignSelected(null); setAssignPrecio('');
      fetchPrices(activeClient.id, pricesPage, pricesSearch);
    } catch (err) {
      setError(err.message);
    } finally {
      setAssignSaving(false);
    }
  };

  const renderPreciosView = () => {
    if (!activeClient) return null;
    return (
      <>
        {/* Same client header as detail */}
        <div className="bg-white shadow-sm border-b border-gray-200 p-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 rounded-full" style={{ backgroundColor: 'rgba(0,153,255,0.08)' }}>
                <Building2 className="w-8 h-8" style={{ color: '#0099FF' }} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{activeClient.razon_social}</h1>
                {activeClient.alias && <p className="text-gray-500">{activeClient.alias}</p>}
                <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                  {activeClient.telefono1 && (
                    <div className="flex items-center"><Phone size={14} className="mr-1" />{activeClient.telefono1}</div>
                  )}
                  {activeClient.mail && (
                    <div className="flex items-center"><Mail size={14} className="mr-1" />{activeClient.mail}</div>
                  )}
                  {activeClient.cif && (
                    <div className="flex items-center"><CreditCard size={14} className="mr-1" />{activeClient.cif}</div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => openEdit(activeClient)}
                className="px-4 py-2 text-white rounded-lg transition-colors duration-200 text-sm flex items-center hover:opacity-90"
                style={{ backgroundColor: '#0099FF' }}
              >
                <Pencil size={14} className="mr-2" /> Editar
              </button>
              <button
                onClick={() => handleDelete(activeClient.id)}
                className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors duration-200 text-sm flex items-center"
              >
                <Trash2 size={14} className="mr-2" /> Eliminar
              </button>
            </div>
          </div>

          {/* Action buttons row */}
          <div className="flex items-center gap-3 mt-4">
            {actionButtons.map(btn => {
              const Icon = btn.icon;
              const isActive = (btn.key === 'precios' && mode === 'precios') || (btn.key === 'pedidos' && mode === 'pedidos') || (btn.key === 'datos' && mode === 'detail') || (btn.key === 'facturacion' && mode === 'facturacion');
              return (
                <button
                  key={btn.key}
                  onClick={btn.onClick}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-200"
                  style={btn.key === 'pedido'
                    ? { borderColor: '#16a34a', backgroundColor: '#16a34a', color: '#ffffff' }
                    : isActive
                    ? { borderColor: '#0099FF', backgroundColor: 'rgba(0,153,255,0.08)', color: '#0077CC' }
                    : { borderColor: '#e5e7eb', backgroundColor: '#ffffff', color: '#374151' }
                  }
                  onMouseEnter={(e) => {
                    if (btn.key === 'pedido') { e.currentTarget.style.backgroundColor = '#15803d'; e.currentTarget.style.borderColor = '#15803d'; }
                    else if (!isActive) { e.currentTarget.style.borderColor = '#0099FF'; e.currentTarget.style.color = '#0077CC'; }
                  }}
                  onMouseLeave={(e) => {
                    if (btn.key === 'pedido') { e.currentTarget.style.backgroundColor = '#16a34a'; e.currentTarget.style.borderColor = '#16a34a'; }
                    else if (!isActive) { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#374151'; }
                  }}
                >
                  <Icon size={16} />
                  {btn.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Precios content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* #12: asignar precio a una referencia NUEVA (sin salir a grabar un pedido) */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            {!showAssignPrice ? (
              <button
                onClick={() => setShowAssignPrice(true)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md text-white hover:opacity-90"
                style={{ backgroundColor: '#0099FF' }}
              >
                <span className="text-lg leading-none">+</span> Asignar precio a nueva referencia
              </button>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-700">Asignar precio a una referencia</span>
                  <button
                    onClick={() => { setShowAssignPrice(false); setAssignSearch(''); setAssignResults([]); setAssignSelected(null); setAssignPrecio(''); }}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Cancelar
                  </button>
                </div>
                <input
                  type="text"
                  value={assignSearch}
                  onChange={(e) => searchAssignProduct(e.target.value)}
                  placeholder="Buscar producto por nombre o referencia..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0099FF]/30"
                />
                {assignResults.length > 0 && !assignSelected && (
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md bg-white">
                    {assignResults.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => { setAssignSelected(p); setAssignResults([]); setAssignSearch(`${p.raiz} — ${p.nombre}`); }}
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                      >
                        <span className="text-gray-400">{p.raiz}</span> {p.nombre}
                      </button>
                    ))}
                  </div>
                )}
                {assignSelected && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Coste del producto: SOLO LECTURA, de referencia para saber el margen.
                        No se puede editar aqui (el coste es global del producto). */}
                    <span className="px-2.5 py-2 text-sm rounded-md bg-gray-100 text-gray-600 border border-gray-200 whitespace-nowrap">
                      Coste: <strong className="text-gray-800">{Number(assignSelected.precio_coste || 0).toFixed(2)} €</strong>
                    </span>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      value={assignPrecio}
                      onChange={(e) => setAssignPrecio(e.target.value)}
                      placeholder="Precio €"
                      className="w-32 px-3 py-2 border border-gray-300 rounded-md text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#0099FF]/30"
                    />
                    <button
                      onClick={saveAssignPrice}
                      disabled={assignSaving || assignPrecio === ''}
                      className="px-4 py-2 text-sm font-medium rounded-md text-white disabled:opacity-50"
                      style={{ backgroundColor: '#16a34a' }}
                    >
                      {assignSaving ? 'Guardando...' : 'Asignar'}
                    </button>
                    {/* Hint de margen: aparece al escribir un precio de venta valido. */}
                    {(() => {
                      const venta = parseFloat(assignPrecio);
                      const coste = Number(assignSelected.precio_coste || 0);
                      if (isNaN(venta) || venta <= 0) return null;
                      const margen = venta > 0 ? ((venta - coste) / venta) * 100 : 0;
                      const color = margen < 0 ? 'text-red-600' : margen < 15 ? 'text-amber-600' : 'text-green-600';
                      return (
                        <span className={`text-sm font-medium whitespace-nowrap ${color}`}>
                          Margen: {margen.toFixed(1)}%
                        </span>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Export/Import + search */}
          <div className="flex items-center justify-between gap-4">
            <div className="relative max-w-md flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={pricesSearch}
                onChange={(e) => setPricesSearch(e.target.value)}
                placeholder="Buscar producto por nombre o código..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleExportExcel}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-200"
                style={{ borderColor: '#e5e7eb', backgroundColor: '#ffffff', color: '#374151' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#0099FF'; e.currentTarget.style.color = '#0077CC'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#374151'; }}
              >
                <Download size={16} />
                Exportar Excel
              </button>
              <button
                onClick={handleExportPdf}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-200"
                style={{ borderColor: '#e5e7eb', backgroundColor: '#ffffff', color: '#374151' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#0099FF'; e.currentTarget.style.color = '#0077CC'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#374151'; }}
              >
                <Download size={16} />
                Exportar PDF
              </button>
              <input type="file" accept=".csv" onChange={handleImportCSV} style={{ display: 'none' }} id="csv-import-input" />
              {isAdmin && (
                <button
                  onClick={() => document.getElementById('csv-import-input').click()}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-200"
                  style={{ borderColor: '#e5e7eb', backgroundColor: '#ffffff', color: '#374151' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#0099FF'; e.currentTarget.style.color = '#0077CC'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#374151'; }}
                >
                  <Upload size={16} />
                  Importar CSV
                </button>
              )}
            </div>
          </div>

          {/* Price table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {loadingPrices ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: '#0099FF' }}></div>
              </div>
            ) : prices.length === 0 ? (
              <div className="text-center py-12">
                <Tag size={28} className="mx-auto mb-2 text-gray-300" />
                <p className="text-sm text-gray-500">No se encontraron precios para este cliente</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Referencia</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Producto</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Precio Coste</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Precio Venta</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">IVA %</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">PVP</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {prices.map(price => {
                      const ivaPercent = IVA_MAP[price.tipo_iva] || 21;
                      const pvp = price.precio * (1 + ivaPercent / 100);
                      const colorClass = getPriceColorClass(price.precio, price.precio_coste);
                      const isEditing = editingPriceId === price.id;

                      return (
                        <tr key={price.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                            {price.producto_raiz || '\u2014'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {price.producto_nombre || '\u2014'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                            {price.precio_coste != null ? `${Number(price.precio_coste).toFixed(2)} €` : '\u2014'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                            {isEditing && isAdmin ? (
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={editingPriceValue}
                                onChange={(e) => setEditingPriceValue(e.target.value)}
                                onBlur={() => handlePriceSave(price.id)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handlePriceSave(price.id);
                                  if (e.key === 'Escape') { setEditingPriceId(null); setEditingPriceValue(''); }
                                }}
                                autoFocus
                                className="w-24 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            ) : (
                              <span
                                className={`inline-block px-2 py-1 rounded ${colorClass} ${isAdmin ? 'cursor-pointer' : ''}`}
                                onClick={() => {
                                  if (!isAdmin) return;
                                  setEditingPriceId(price.id);
                                  setEditingPriceValue(String(price.precio ?? ''));
                                }}
                              >
                                {price.precio != null ? `${Number(price.precio).toFixed(2)} €` : '\u2014'}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                            {ivaPercent}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium text-right">
                            {price.precio != null ? `${pvp.toFixed(2)} €` : '\u2014'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Prices pagination */}
            {pricesTotalPages > 1 && (
              <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                <span className="text-xs text-gray-500">{pricesTotal} productos</span>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setPricesPage(p => Math.max(1, p - 1))}
                    disabled={pricesPage === 1}
                    className="text-sm text-gray-500 disabled:opacity-40"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="text-xs text-gray-500">Pág {pricesPage} de {pricesTotalPages}</span>
                  <button
                    onClick={() => setPricesPage(p => Math.min(pricesTotalPages, p + 1))}
                    disabled={pricesPage === pricesTotalPages}
                    className="text-sm text-gray-500 disabled:opacity-40"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  // ══════════════════════════════════════════════════
  // ── RENDER: Facturacion View ──
  // ══════════════════════════════════════════════════
  const renderFacturacionView = () => {
    if (!activeClient) return null;

    const formatCurrency = (value) => {
      if (value == null) return '\u2014';
      return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value);
    };

    const formatDateStr = (dateStr) => {
      if (!dateStr) return '\u2014';
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const getInvoiceNumber = (factura) => {
      if (!factura.nombre_archivo) return `#${factura.id}`;
      return factura.nombre_archivo.replace(/\.pdf$/i, '');
    };

    return (
      <>
        {/* Client header with actions */}
        <div className="bg-white shadow-sm border-b border-gray-200 p-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 rounded-full" style={{ backgroundColor: 'rgba(0,153,255,0.08)' }}>
                <Building2 className="w-8 h-8" style={{ color: '#0099FF' }} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{activeClient.razon_social}</h1>
                {activeClient.alias && <p className="text-gray-500">{activeClient.alias}</p>}
                <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                  {activeClient.telefono1 && (<div className="flex items-center"><Phone size={14} className="mr-1" />{activeClient.telefono1}</div>)}
                  {activeClient.mail && (<div className="flex items-center"><Mail size={14} className="mr-1" />{activeClient.mail}</div>)}
                  {activeClient.cif && (<div className="flex items-center"><CreditCard size={14} className="mr-1" />{activeClient.cif}</div>)}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button onClick={() => openEdit(activeClient)} className="px-4 py-2 text-white rounded-lg transition-colors duration-200 text-sm flex items-center hover:opacity-90" style={{ backgroundColor: '#0099FF' }}>
                <Pencil size={14} className="mr-2" /> Editar
              </button>
              <button onClick={() => handleDelete(activeClient.id)} className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors duration-200 text-sm flex items-center">
                <Trash2 size={14} className="mr-2" /> Eliminar
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-4">
            {actionButtons.map(btn => {
              const Icon = btn.icon;
              const isActive = (btn.key === 'precios' && mode === 'precios') || (btn.key === 'pedidos' && mode === 'pedidos') || (btn.key === 'datos' && mode === 'detail') || (btn.key === 'facturacion' && mode === 'facturacion');
              return (
                <button key={btn.key} onClick={btn.onClick}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-200"
                  style={btn.key === 'pedido' ? { borderColor: '#16a34a', backgroundColor: '#16a34a', color: '#ffffff' } : isActive ? { borderColor: '#0099FF', backgroundColor: 'rgba(0,153,255,0.08)', color: '#0077CC' } : { borderColor: '#e5e7eb', backgroundColor: '#ffffff', color: '#374151' }}
                  onMouseEnter={(e) => { if (btn.key === 'pedido') { e.currentTarget.style.backgroundColor = '#15803d'; e.currentTarget.style.borderColor = '#15803d'; } else if (!isActive) { e.currentTarget.style.borderColor = '#0099FF'; e.currentTarget.style.color = '#0077CC'; }}}
                  onMouseLeave={(e) => { if (btn.key === 'pedido') { e.currentTarget.style.backgroundColor = '#16a34a'; e.currentTarget.style.borderColor = '#16a34a'; } else if (!isActive) { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#374151'; }}}
                >
                  <Icon size={16} /> {btn.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Facturacion content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Search bar */}
          <div className="flex items-center justify-between gap-4">
            <div className="relative max-w-md flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={facturasSearch}
                onChange={(e) => setFacturasSearch(e.target.value)}
                placeholder="Buscar por N\u00B0 de factura..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <p className="text-sm text-gray-500">{facturasTotal} facturas</p>
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">N\u00B0 Factura</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Base Imponible</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loadingFacturas ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                        <p className="text-sm text-gray-500 mt-2">Cargando facturas...</p>
                      </td>
                    </tr>
                  ) : clientFacturas.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center">
                        <Receipt size={28} className="mx-auto mb-2 text-gray-300" />
                        <p className="text-sm text-gray-500">No se encontraron facturas para este cliente</p>
                      </td>
                    </tr>
                  ) : (
                    clientFacturas.map((factura) => (
                      <tr key={factura.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900">{getInvoiceNumber(factura)}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-500">{formatDateStr(factura.fecha)}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className="text-sm text-gray-600">{formatCurrency(factura.base)}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className="text-sm font-medium text-gray-900">{formatCurrency(factura.final)}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => setPreviewFactura(factura)}
                              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-blue-600 transition-colors"
                              title="Vista previa"
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              onClick={() => openFacturaPdf(factura)}
                              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-blue-600 transition-colors"
                              title="Ver PDF"
                            >
                              <ExternalLink size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {facturasTotalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50">
                <p className="text-sm text-gray-500">
                  P\u00E1gina {facturasPage} de {facturasTotalPages} \u00B7 {facturasTotal} facturas
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setFacturasPage((p) => Math.max(1, p - 1))}
                    disabled={facturasPage === 1}
                    className="p-1.5 rounded-lg border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    onClick={() => setFacturasPage((p) => Math.min(facturasTotalPages, p + 1))}
                    disabled={facturasPage === facturasTotalPages}
                    className="p-1.5 rounded-lg border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Preview Modal */}
        {previewFactura && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setPreviewFactura(null)}>
            <div className="bg-white rounded-xl shadow-xl w-[90vw] max-w-4xl h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
                <div>
                  <p className="text-sm font-semibold text-gray-800">Factura: {getInvoiceNumber(previewFactura)}</p>
                  <p className="text-xs text-gray-500">Fecha: {formatDateStr(previewFactura.fecha)} \u00B7 Total: {formatCurrency(previewFactura.final)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openFacturaPdf(previewFactura)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-white hover:opacity-90 transition-colors"
                    style={{ backgroundColor: '#0099FF' }}
                  >
                    <ExternalLink size={12} />
                    Abrir PDF
                  </button>
                  <button
                    onClick={() => setPreviewFactura(null)}
                    className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-hidden">
                {previewPdfUrl ? (
                  <iframe
                    src={previewPdfUrl}
                    className="w-full h-full border-0"
                    title={`Factura ${getInvoiceNumber(previewFactura)}`}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-sm text-gray-400">
                    Cargando factura…
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // ══════════════════════════════════════════════════
  // ── MAIN RENDER ──
  // ══════════════════════════════════════════════════
  return (
    <div className="h-full flex">
      {/* SIDEBAR */}
      <aside className="w-80 bg-white border-r border-gray-200 flex flex-col flex-shrink-0">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Gestión de Clientes</h1>
          <p className="text-sm text-gray-500 mb-4">Administración de clientes y sedes</p>
          <div className="flex gap-2">
            <button
              onClick={() => setShowImportModal(true)}
              className="flex-1 px-3 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-1"
            >
              <Upload size={14} /> Importar
            </button>
            <CreateButton onClick={openCreate} className="flex-1">Nuevo Cliente</CreateButton>
          </div>
        </div>

        {activeClient && linkedClients.length > 0 ? (
          /* ── LINKED CLINICS VIEW ── */
          <>
            <div className="p-4 border-b border-gray-200">
              <button
                onClick={() => { setActiveClient(null); setMode('detail'); }}
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors mb-3"
              >
                <ArrowLeft size={16} />
                Volver a todos los clientes
              </button>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Clínicas asociadas a</p>
              <p className="text-sm font-medium text-gray-800 mt-1">{activeClient.razon_social}</p>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {/* Active client label */}
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-3 mb-1">
                {(!activeClient.clinica_matriz || activeClient.clinica_matriz === 0) ? 'Clínica Matriz' : 'Clínica Asociada'}
              </p>
              {/* Active client highlighted */}
              <button
                key={activeClient.id}
                onClick={() => handleClientClick(activeClient)}
                className="w-full text-left px-3 py-3 rounded-lg transition-all duration-200 border mb-1"
                style={{ borderColor: '#0099FF', backgroundColor: 'rgba(0,153,255,0.08)', color: '#0077CC' }}
              >
                <div className="flex items-center gap-2">
                  <Building2 size={14} style={{ color: '#0099FF' }} />
                  <p className="text-sm font-medium truncate">{activeClient.razon_social}</p>
                </div>
                <div className="flex items-center gap-2 mt-1 ml-6">
                  {activeClient.cif && <span className="text-xs text-gray-400 font-mono">{activeClient.cif}</span>}
                  {activeClient.localidad && <span className="text-xs text-gray-400">&middot; {activeClient.localidad}</span>}
                </div>
              </button>
              {/* Linked clients label */}
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-3 mt-3 mb-1">
                {(!activeClient.clinica_matriz || activeClient.clinica_matriz === 0) ? 'Clínicas Asociadas' : 'Grupo clínico'}
              </p>
              {/* Linked clients */}
              {linkedClients.map(lc => (
                <button
                  key={lc.id}
                  onClick={() => handleClientClick(lc)}
                  className="w-full text-left px-3 py-3 rounded-lg transition-all duration-200 border mb-1 hover:bg-gray-50"
                  style={{ borderColor: 'transparent' }}
                >
                  <div className="flex items-center gap-2">
                    <Users size={14} className="text-gray-400" />
                    <p className="text-sm font-medium text-gray-800 truncate">{lc.razon_social}</p>
                    {(!lc.clinica_matriz || lc.clinica_matriz === 0) && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">MATRIZ</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1 ml-6">
                    {lc.cif && <span className="text-xs text-gray-400 font-mono">{lc.cif}</span>}
                    {lc.localidad && <span className="text-xs text-gray-400">&middot; {lc.localidad}</span>}
                  </div>
                </button>
              ))}
            </div>
          </>
        ) : (
          /* ── ALL CLIENTS LIST ── */
          <>
            {/* Search */}
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar cliente..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <p className="text-xs text-gray-400 mt-2">{totalClients} clientes</p>
            </div>

            {/* Client list */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-2">
                {loading && clients.length === 0 ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: '#0099FF' }}></div>
                  </div>
                ) : clients.length === 0 ? (
                  <div className="text-center py-8">
                    <Building2 size={28} className="mx-auto mb-2 text-gray-300" />
                    <p className="text-sm text-gray-500">No se encontraron clientes</p>
                  </div>
                ) : (
                  clients.map(client => (
                    <button
                      key={client.id}
                      onClick={() => handleClientClick(client)}
                      className="w-full text-left px-3 py-3 rounded-lg transition-all duration-200 border mb-1"
                      style={activeClient?.id === client.id
                        ? { borderColor: '#0099FF', backgroundColor: 'rgba(0,153,255,0.08)', color: '#0077CC' }
                        : { borderColor: 'transparent' }
                      }
                    >
                      <p className="text-sm font-medium text-gray-800 truncate">{client.razon_social}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {client.cif && <span className="text-xs text-gray-400 font-mono">{client.cif}</span>}
                        {client.localidad && <span className="text-xs text-gray-400">&middot; {client.localidad}</span>}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="p-3 border-t border-gray-200 flex items-center justify-between">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="text-sm text-gray-500 disabled:opacity-40"
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="text-xs text-gray-500">Pág {page} de {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="text-sm text-gray-500 disabled:opacity-40"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}
      </aside>

      {/* MAIN PANEL */}
      <main className="flex-1 overflow-hidden flex flex-col">
        {/* Error banner */}
        {error && (
          <div className="bg-red-50 border-b border-red-200 text-red-700 p-3 flex items-center justify-between flex-shrink-0">
            <span className="text-sm">{error}</span>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-600"><X size={16} /></button>
          </div>
        )}

        {mode === 'form' && renderFormView()}
        {mode === 'pedidos' && renderPedidosView()}
        {mode === 'detail' && renderDetailView()}
        {mode === 'precios' && renderPreciosView()}
        {mode === 'facturacion' && renderFacturacionView()}
      </main>

      {/* CSV Import Modal */}
      <CsvImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Importar Clientes"
        description="Formato CSV (delimitador ;): CODIGO_CLIENTE;CODIGO_COMERCIAL;RAZON_SOCIAL;ALIAS;CALLE;LOCALIDAD;PROVINCIA;C_POSTAL;CIF;TELEFONO1;TELEFONO2;MAIL;FAX;CONTACTO;CLINICA_MATRIZ"
        onImport={handleImportClients}
      />
    </div>
  );
};

export default ClientManagement;
